// src/components/MentorDashboard/TrainingMembers.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  List,
  Row,
  Col,
  Typography,
  Progress,
  Button,
  Badge,
  Avatar,
  Empty,
  message,
  Tooltip,
  Tag,
} from "antd";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import { baseImageUrl } from "Common/Components/baseImageUrl";
import api from "Common/Client/Interceptor";

const { Title, Text } = Typography;

const TrainingMembers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { trainingId, darkMode = false } = location.state || {};
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [issuingCertificate, setIssuingCertificate] = useState({});
  const [repeatingTraining, setRepeatingTraining] = useState({});
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchTrainingMembers();
  }, [trainingId, location.state]);

  const fetchTrainingMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`Training/Members-With-Progress?trainingId=${trainingId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
      });
      setLearners(response.data);
      if (location.state?.trainingTitle) {
        setTrainingTitle(location.state.trainingTitle);
      }
    } catch (error) {
      console.error("Error fetching training members:", error);
      message.error("Failed to fetch training members");
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (progress) => {
    if (progress < 50) return "needs-attention";
    if (progress >= 90) return "near-completion";
    return "active";
  };

  const handleIssueCertificate = async (trainingId, groupMemberId) => {
    try {
      setIssuingCertificate((prev) => ({ ...prev, [groupMemberId]: true }));

      await api.post(
        `TrainingCertificate/Create?trainingId=${trainingId}&groupMemberId=${groupMemberId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        }
      );

      setLearners((prev) =>
        prev.map((learner) =>
          learner.id === groupMemberId ? { ...learner, isCertificateGiven: true } : learner
        )
      );

      message.success("Certificate issued successfully");
      fetchTrainingMembers();
    } catch (error) {
      console.error("Error issuing certificate:", error);
      message.error("Failed to issue certificate");
    } finally {
      setIssuingCertificate((prev) => ({ ...prev, [groupMemberId]: false }));
    }
  };

  const handleRepeatTraining = async (trainingId, groupMemberId) => {
    try {
      setRepeatingTraining((prev) => ({ ...prev, [groupMemberId]: true }));

      const response = await api.post(
        `Training/Mentor/Repeat-Training?trainingId=${trainingId}&groupMemberId=${groupMemberId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        }
      );

      message.success("Training repeated successfully");
      fetchTrainingMembers();
    } catch (error) {
      console.error("Error repeating training:", error);
      message.error("Failed to repeat training");
    } finally {
      setRepeatingTraining((prev) => ({ ...prev, [groupMemberId]: false }));
    }
  };

  return (
    <Card
      className="card-professional"
      loading={loading}
      title={
        <span style={{ color: darkMode ? "rgba(255, 255, 255, 0.85)" : undefined }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          {trainingTitle ? `Learners for ${trainingTitle}` : "Training Learners"}
        </span>
      }
      style={{
        borderRadius: 12,
        marginBottom: 24,
        backgroundColor: darkMode ? "#1f1f1f" : "#fff",
        borderColor: darkMode ? "#303030" : "#f0f0f0",
      }}
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      {learners.length === 0 ? (
        <Empty
          description={
            <Text style={{ color: darkMode ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.45)" }}>
              No Learners in this Training
            </Text>
          }
          style={{
            padding: 40,
            backgroundColor: darkMode ? "#2a2a2a" : "#fafafa",
            borderRadius: 8,
          }}
        />
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
          dataSource={learners}
          renderItem={(learner) => {
            const status = getStatus(learner.progress);
            const scoreColor = learner.averageScore >= 80 ? "#52c41a" : "#ff4d4f";

            return (
              <List.Item>
                <Card
                  hoverable
                  style={{
                    backgroundColor: darkMode ? "#2a2a2a" : "#fff",
                    borderColor: darkMode ? "#404040" : "#f0f0f0",
                    transition: "all 0.3s",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Ribbon badge based on trainingStatus */}
                  {learner.trainingStatus === 2 && (
                    <Tag
                      color="green"
                      style={{
                        position: "absolute",
                        top: 16,
                        right: -30,
                        transform: "rotate(45deg)",
                        width: 120,
                        textAlign: "center",
                      }}
                    >
                      Certificated
                    </Tag>
                  )}
                  {learner.trainingStatus === 3 && (
                    <Tag
                      color="red"
                      style={{
                        position: "absolute",
                        top: 16,
                        right: -30,
                        transform: "rotate(45deg)",
                        width: 120,
                        textAlign: "center",
                      }}
                    >
                      Failed
                    </Tag>
                  )}

                  <Row gutter={16} align="middle">
                    <Col xs={6}>
                      <Badge
                        dot
                        color={
                          status === "needs-attention"
                            ? "#ff4d4f"
                            : status === "near-completion"
                            ? "#52c41a"
                            : "#1890ff"
                        }
                      >
                        <Avatar
                          size={64}
                          src={
                            learner.profileImage ? `${baseImageUrl}${learner.profileImage}` : null
                          }
                          icon={!learner?.profileImage && <UserOutlined />}
                        />
                      </Badge>
                    </Col>
                    <Col xs={18}>
                      <Title
                        level={5}
                        style={{
                          marginBottom: 4,
                          color: darkMode ? "rgba(255, 255, 255, 0.85)" : undefined,
                        }}
                      >
                        {learner.firstName} {learner.lastName}
                      </Title>
                      <Progress
                        percent={learner.progress}
                        strokeColor={
                          status === "needs-attention"
                            ? "#ff4d4f"
                            : status === "near-completion"
                            ? "#52c41a"
                            : "#1890ff"
                        }
                        showInfo={false}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text
                          type="secondary"
                          style={{
                            color: darkMode ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.45)",
                          }}
                        >
                          {learner.progress}% Progress
                        </Text>
                        <Text strong style={{ color: scoreColor }}>
                          Score: {learner.averageScore}%
                        </Text>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        {learner.action === 1 && (
                          <>
                            <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                              {learner.alertText}
                            </Text>
                          </>
                        )}

                        {learner.action === 2 && (
                          <div>
                            <Button
                              type="primary"
                              size="small"
                              loading={issuingCertificate[learner.id]}
                              onClick={() => handleIssueCertificate(learner.trainingId, learner.id)}
                              style={{ marginRight: 8 }}
                            >
                              Issue Certificate
                            </Button>
                            {learner.alertText && (
                              <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                                {learner.alertText}
                              </Text>
                            )}
                          </div>
                        )}

                        {learner.action === 3 && (
                          <div>
                            <Button
                              type="primary"
                              size="small"
                              loading={repeatingTraining[learner.id]}
                              onClick={() => handleRepeatTraining(learner.trainingId, learner.id)}
                              style={{ marginRight: 8 }}
                            >
                              Repeat Training
                            </Button>
                            {learner.alertText && (
                              <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                                {learner.alertText}
                              </Text>
                            )}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </Card>
  );
};

export default TrainingMembers;
